<template>
  <div>
    <form-card
      v-if="hasAddr"
      :labelText="addrLabel"
    >
      <el-row :gutter="20">
        <el-col :xs="24" :md="6">
          <text-field
            v-model="formData.zip"
            :base="base"
            :hasDesc="false"
            name="zip"
            :placeholderText="getInputPlaceholder('zip')"
            :maxlength="16"
          >
          </text-field>
        </el-col>
        <el-col :xs="24" :md="18">
          <text-field
            v-model="formData.area"
            :base="base"
            name="area"
            :required="required"
            :hasDesc="true"
            :hasTrans="true"
            :oriData="oriData"
            :transData="transData"
            :placeholderText="getInputPlaceholder('area')"
            :maxlength="255"
          >
          </text-field>
        </el-col>
        <el-col :xs="24">
          <text-field
            v-model="formData.address"
            :base="base"
            name="address"
            :required="required"
            :hasDesc="true"
            :hasTrans="true"
            :oriData="oriData"
            :transData="transData"
            :placeholderText="getInputPlaceholder('address')"
            :maxlength="255"
          >
          </text-field>
        </el-col>
        <el-col :xs="24" v-if="hasLocationDetail">
          <text-field
            v-model="formData.location_detail"
            :base="base"
            name="location_detail"
            :hasDesc="true"
            :hasTrans="true"
            :show-auto-translate="true"
            :oriData="oriData"
            :transData="transData"
            :placeholderText="$t('placeholder_content_format_hint', { field_name: getLabelText(base, 'location_detail') })"
            :grammar-config="{
              content: formData.location_detail,
              template: GrammarCheckerTemplate['BranchLocationDescription'],
              currencyCode: formData.currency_code
            }"
          >
          </text-field>
        </el-col>
      </el-row>
      <slot name="afterAddr"></slot>
      <el-form-item>
        <el-tooltip
          class="item"
          effect="dark"
          placement="top"
          :content="$t('api.maps.show.tooltip')"
        >
          <el-button
            type="primary"
            class="form-action-btn"
            @click="initMap(updMapByAddr)"
            :disabled="isValidAddr"
          >
            {{ $t('api.maps.updMapByAddr') }}
          </el-button>
        </el-tooltip>
      </el-form-item>
      <slot name="appendAddr"></slot>
    </form-card>
    <form-card
      :labelText="$t('api.maps.title')"
    >
      <el-row
        :gutter="20"
      >
        <el-col :xs="24" :md="12">
          <text-field
            v-model.number="formData.lat"
            :base="base"
            name="lat"
            :labelText="$t('api.maps.fields.lat.label')"
            :placeholderText="getInputPlaceholder('lat', 'maps')"
            :rules="latRule"
            maxlength="17"
          >
          </text-field>
        </el-col>
        <el-col :xs="24" :md="12">
          <text-field
            v-model.number="formData.lng"
            :base="base"
            name="lng"
            :labelText="$t('api.maps.fields.lng.label')"
            :placeholderText="getInputPlaceholder('lng', 'maps')"
            :rules="lngRule"
            maxlength="17"
          >
          </text-field>
        </el-col>
        <el-col :xs="24" :md="12" v-if="this.hasPlaceID">
          <text-field
            v-model.number="formData.google_place_id"
            :base="base"
            name="google_place_id"
            :labelText="$t('api.maps.fields.google_place_id.label')"
            :placeholderText="getInputPlaceholder('google_place_id', 'maps')"
          >
          </text-field>
        </el-col>
        <slot name="afterGPS"></slot>
        <el-col :xs="24">
          <el-form-item>
            <el-tooltip
              class="item"
              effect="dark"
              placement="top"
              :content="$t('api.maps.show.tooltip')"
            >
              <el-button
                v-show="isShowGmap"
                type="primary"
                class="form-action-btn"
                @click="updMapByGPS"
                :disabled="isValidGPS"
              >
                {{ $t('api.maps.updMapByGPS') }}
              </el-button>
            </el-tooltip>
          </el-form-item>
        </el-col>
        <el-col
          :xs="24"
          v-show="!isShowGmap"
        >
          <el-tooltip
            class="item"
            effect="dark"
            placement="top"
            :content="$t('api.maps.show.tooltip')"
          >
            <el-button
              type="primary"
              icon="el-icon-map-location"
              @click="initMap"
            >
              {{ $t('api.maps.show.btnText') }}
            </el-button>
          </el-tooltip>
        </el-col>
      </el-row>
      <el-row
        :gutter="20"
        v-show="isShowGmap"
        v-loading="mapLoading"
        :element-loading-text="$t('api.maps.loading')"
      >
        <el-col :xs="24">
          <el-form-item
            :label="$t('api.maps.search.label')"
          >
            <div class="form-item el-input el-input--medium el-input--prefix">
              <gmap-autocomplete
                :value="placeDesc"
                @keydown.enter.prevent.native="searchKeyDown"
                @place_changed="setPlace"
                selectFirstOnEnter
                :placeholder="$t('api.maps.search.msg')"
                class="el-input__inner"
              >
              </gmap-autocomplete>
              <span class="el-input__prefix"><i class="el-input__icon el-icon-search"></i></span>
            </div>
          </el-form-item>
        </el-col>
        <el-col :xs="24">
          <gmap-map
            ref="mapRef"
            :center="mapCenterPos"
            :zoom="16"
            style="width: 100%; height: 350px; margin-top: 25px;"
            @click="mapClick"
          >
            <gmap-info-window
              :options="infoOptions"
              :position="infoWindowPos"
              :opened="infoWinOpen"
              @closeclick="infoWinOpen=false"
            >
              <span v-html="infoContent"></span>
            </gmap-info-window>
            <gmap-marker
              ref="curMarker"
              :position="markerPos"
              :clickable="true"
              @click="setInfoWindow"
            >
            </gmap-marker>
          </gmap-map>
        </el-col>
      </el-row>
    </form-card>
  </div>
</template>

<script>
import { GrammarCheckerTemplate } from '@/enums/aiRelativeTools'
import { getTransText, getLabelText } from '@/utils/i18n'
export default {
  name: 'gmap-loc',
  props: {
    base: {
      type: String,
      default: 'maps'
    },
    formData: {
      type: Object,
      default: () => {}
    },
    hasAddr: {
      type: Boolean,
      default: false
    },
    addrLabel: {
      type: String,
      default: ''
    },
    countryCode: {
      type: String,
      default: ''
    },
    hasLocationDetail: {
      type: Boolean,
      default: false
    },
    hasPlaceID: {
      type: Boolean,
      default: false
    },
    oriData: null,
    transData: {
      type: Array,
      default: () => []
    },
    getPlaceholder: {
      type: Function,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      GrammarCheckerTemplate,
      mapLoading: true,
      isShowGmap: false,
      placeDesc: '',
      infoOptions: {
        pixelOffset: {
          width: 0,
          height: -35
        }
      },
      infoWinOpen: true,
      infoContent: '',
      infoWindowPos: null,
      markerPos: null,
      geocoder: null,
      mapCenterPos: {
        lat: 25.0469586,
        lng: 121.5397366
      }
    }
  },
  created () {
  },
  mounted () {
  },
  computed: {
    latRule () {
      return this.getValidRule(this.validLatVal, 'lat')
    },
    lngRule () {
      return this.getValidRule(this.validLngVal, 'lng')
    },
    isValidAddr () {
      let area = this.formData.area
      let address = this.formData.address
      if (this.getPlaceholder) {
        area = area || this.getPlaceholder('area', this.base)
        address = address || this.getPlaceholder('address', this.base)
      }
      return area === '' || address === ''
    },
    isValidGPS () {
      let d = this.formData
      if (!this.isValidCoordinates(d)) {
        if (d.branch_place) {
          return !this.isValidCoordinates(d.branch_place)
        }
        return true
      }
      return false
    }
  },
  methods: {
    getLabelText,
    getTransText,
    initMap (afterInit) {
      this.isShowGmap = true
      this.$refs.mapRef.$mapPromise.then((map) => {
        this.geocoder = new window.google.maps.Geocoder()
        this.mapLoading = false
        if (typeof afterInit === 'function') {
          afterInit()
        } else {
          this.updMapByGPS()
        }
      }).catch(err => {
        console.log(err)
      })
    },
    label (field) {
      return this.$t('api.maps.fields.' + field + '.label')
    },
    getValidRule (validVal, field) {
      return [
        {
          required: this.required,
          trigger: 'blur',
          validator: (rule, value, callback) => {
            return this.valid(rule, value, callback, validVal, field)
          }
        }
      ]
    },
    valid (rule, value, callback, validVal, field) {
      if (!this.required && (value === null || value === '')) {
        return callback()
      }

      if (!this.isNumber(value)) {
        return callback(new Error(this.$t('api.maps.error.number', {
          label: this.label(field)
        })))
      }

      if (!validVal(value)) {
        return callback(new Error(this.getInputPlaceholder(field, 'maps')))
      }

      callback()
    },
    getInputPlaceholder (field, fbase) {
      let base = fbase || this.base
      if (this.getPlaceholder) {
        let placeholder = this.getPlaceholder(field, base)
        if (placeholder !== 'undefined') {
          return placeholder
        }
      }
      return this.getTransText(field, 'placeholder', base)
    },
    isValidCoordinates (data) {
      return this.isNumber(data.lat) &&
        this.validLatVal(data.lat) &&
        this.isNumber(data.lng) &&
        this.validLngVal(data.lng)
    },
    isNumber (v) {
      if (!/^[-+]?[0-9]*\.?[0-9]+$/.test(v)) {
        return false
      }
      let n = parseFloat(v)
      return !isNaN(n)
    },
    isBetweenRange (value, n1, n2) {
      return n1 <= value && n2 >= value
    },
    validLatVal (v) {
      return this.validRange(v, -90, 90)
    },
    validLngVal (v) {
      return this.validRange(v, -180, 180)
    },
    validRange (v, min, max) {
      return !!(this.isBetweenRange(parseFloat(v), min, max))
    },
    errorLog (data) {
      console.log('Invalid GPS Lat/Lng')
      console.log(data)
    },
    updMapByGPS () {
      const d = this.formData
      let place = d
      if (d.branch_place) {
        place = d.branch_place
      }
      if (!this.isValidCoordinates(place)) {
        return this.errorLog(place)
      }

      const location = {
        lat () {
          return parseFloat(place.lat)
        },
        lng () {
          return parseFloat(place.lng)
        }
      }
      this.setPos(location)
      this.setInfoWindow()
    },
    async updMapByAddr (isOnlyAddr) {
      const d = this.formData
      const sname = isOnlyAddr ? '' : d.branch_name || d.name
      const q = [sname, d.area, d.address]
        .map(s => s && s.trim())
        .filter(Boolean)
        .join(' ')

      try {
        const countryCode = this.countryCode || this.formData.country_code
        const { places } = await window.google.maps.places.Place.searchByText({
          textQuery: q,
          maxResultCount: 1,
          region: countryCode,
          fields: ['id', 'displayName', 'formattedAddress', 'location']
        })

        if (places.length > 0) {
          this.setPlace(places[0])
        } else {
          if (sname !== '') {
            this.updMapByAddr(true)
          } else {
            this.$message({
              message: this.$t('api.noData'),
              type: 'warning',
              duration: 10000,
              showClose: true
            })
          }
        }
      } catch (err) {
        this.$message({
          message: 'google.maps.places.Place.searchByText error: ' + (err.message || err),
          type: 'error',
          duration: 10000,
          showClose: true
        })
      }
    },
    searchKeyDown (event) {
      // do nothing to prevent default enter event
    },
    setPos (location) {
      this.mapCenterPos = {
        lat: location.lat(),
        lng: location.lng()
      }
      this.markerPos = this.mapCenterPos
    },
    setFormData (placeId) {
      this.formData.lat = this.markerPos.lat
      this.formData.lng = this.markerPos.lng
      if (placeId) {
        this.formData.google_place_id = placeId
      }
    },
    setPlace (place) {
      this.setPos(place.geometry ? place.geometry.location : place.location)
      this.setInfoWindow(place.formatted_address || place.formattedAddress)
      this.setFormData(place.place_id ? place.place_id : place.id)
    },
    async mapClick (event) {
      this.setPos(event.latLng)
      const place = await this.setInfoWindow()
      this.setFormData(event.placeId || (place && place.place_id))
    },
    async getPlace () {
      try {
        const { results } = await this.geocoder.geocode({ location: this.markerPos })
        if (results && results[0]) {
          return results[0]
        } else {
          console.warn('No results found for the given coordinates.')
          return null
        }
      } catch (error) {
        console.error('Geocoder failed due to: ' + error)
        return null
      }
    },
    async setInfoWindow (addr) {
      if (this.infoWinOpen) {
        this.infoWinOpen = false
      }
      let place = null
      let placeAddr = addr
      if (!addr) {
        place = await this.getPlace()
        if (place) {
          placeAddr = place.formatted_address
        }
      }
      this.showInfoWindow(placeAddr)
      return place
    },
    showInfoWindow (addr) {
      this.infoContent = this.$t('api.maps.addr') + ': ' + addr +
        '<br/>' + this.label('lat') + ': ' + this.markerPos.lat +
        '<br/>' + this.label('lng') + ': ' + this.markerPos.lng
      this.infoWindowPos = this.markerPos
      this.infoWinOpen = true
    }
  }
}
</script>
